import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Sms as SmsIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  Delete as DeleteIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { adminAPI } from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';

const COLORS = ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0'];

const SystemHealth = () => {
  const [metrics, setMetrics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [feedbackAnalytics, setFeedbackAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // Log filters
  const [logLevel, setLogLevel] = useState('');
  const [logCategory, setLogCategory] = useState('');
  const [logLimit, setLogLimit] = useState(50);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const loadMetrics = useCallback(async () => {
    try {
      const res = await adminAPI.getMetrics({ days: 30 });
      setMetrics(res.data);
    } catch (err) {
      console.error('Failed to load metrics:', err);
      setError('Failed to load metrics');
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = { limit: logLimit };
      if (logLevel) params.level = logLevel;
      if (logCategory) params.category = logCategory;
      
      const res = await adminAPI.getLogs(params);
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to load logs:', err);
    }
    setLogsLoading(false);
  }, [logLevel, logCategory, logLimit]);

  const loadFeedbackAnalytics = useCallback(async () => {
    try {
      const res = await adminAPI.getFeedbackAnalytics({ days: 30 });
      setFeedbackAnalytics(res.data);
    } catch (err) {
      console.error('Failed to load feedback analytics:', err);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    await Promise.all([loadMetrics(), loadLogs(), loadFeedbackAnalytics()]);
    setLoading(false);
  }, [loadMetrics, loadLogs, loadFeedbackAnalytics]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    loadLogs();
  }, [logLevel, logCategory, logLimit, loadLogs]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(loadAll, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, loadAll]);

  const handleCleanupLogs = async () => {
    if (!window.confirm('Delete logs older than 7 days?')) return;
    try {
      const res = await adminAPI.cleanupLogs(7);
      alert(`Deleted ${res.data.deleted} old log entries`);
      loadLogs();
    } catch (err) {
      alert('Failed to cleanup logs');
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'error': return <ErrorIcon color="error" fontSize="small" />;
      case 'warn': return <WarningIcon color="warning" fontSize="small" />;
      default: return <InfoIcon color="info" fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const today = metrics?.today || {};
  const history = metrics?.history || [];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4">System Health</Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant={autoRefresh ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadAll}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Today's Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>Today's Messages</Typography>
                  <Typography variant="h4">{today.total_count || 0}</Typography>
                </Box>
                <TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                <Chip icon={<SmsIcon />} label={today.sms_count || 0} size="small" />
                <Chip icon={<PhoneIcon />} label={today.voice_count || 0} size="small" />
                <Chip icon={<EmailIcon />} label={today.email_count || 0} size="small" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>High Risk</Typography>
                  <Typography variant="h4" color="error.main">{today.high_risk_count || 0}</Typography>
                </Box>
                <WarningIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {today.total_count ? 
                  `${Math.round((today.high_risk_count / today.total_count) * 100)}% of messages` 
                  : 'No messages yet'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>Avg Processing</Typography>
                  <Typography variant="h4">{Math.round(today.avg_processing_ms) || 0}ms</Typography>
                </Box>
                <SpeedIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Max: {today.max_processing_ms || 0}ms
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>Errors Today</Typography>
                  <Typography variant="h4" color={today.error_count > 0 ? 'error.main' : 'success.main'}>
                    {today.error_count || 0}
                  </Typography>
                </Box>
                {today.error_count > 0 ? 
                  <ErrorIcon color="error" sx={{ fontSize: 40 }} /> :
                  <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                }
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 4 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Volume & Trends" />
          <Tab label="System Logs" />
          <Tab label="Feedback Analytics" />
        </Tabs>

        {/* Volume & Trends Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            {history.length > 0 ? (
              <>
                <Typography variant="h6" gutterBottom>Message Volume (30 days)</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={history.slice().reverse()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(d) => format(new Date(d), 'MM/dd')} />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="sms_count" name="SMS" fill="#2196f3" stackId="a" />
                    <Bar dataKey="voice_count" name="Voice" fill="#4caf50" stackId="a" />
                    <Bar dataKey="email_count" name="Email" fill="#ff9800" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </>
            ) : (
              <Typography color="text.secondary">No historical data available yet.</Typography>
            )}
          </Box>
        )}

        {/* System Logs Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="h6">System Logs</Typography>
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 100 } }}>
                  <InputLabel>Level</InputLabel>
                  <Select value={logLevel} onChange={(e) => setLogLevel(e.target.value)} label="Level">
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="info">Info</MenuItem>
                    <MenuItem value="warn">Warning</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 120 } }}>
                  <InputLabel>Category</InputLabel>
                  <Select value={logCategory} onChange={(e) => setLogCategory(e.target.value)} label="Category">
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                    <MenuItem value="voice">Voice</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="auth">Auth</MenuItem>
                    <MenuItem value="system">System</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="webhook">Webhook</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 80 } }}>
                  <InputLabel>Limit</InputLabel>
                  <Select value={logLimit} onChange={(e) => setLogLimit(e.target.value)} label="Limit">
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                    <MenuItem value={200}>200</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title="Delete logs older than 7 days">
                  <IconButton onClick={handleCleanupLogs} color="error" size="small">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {logsLoading && <LinearProgress sx={{ mb: 2 }} />}

            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={50}>Level</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} width={100}>Category</TableCell>
                    <TableCell width={150}>Time</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }} width={150}>User</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow 
                      key={log.id} 
                      sx={{ 
                        bgcolor: log.level === 'error' ? 'error.50' : 
                                 log.level === 'warn' ? 'warning.50' : 'inherit'
                      }}
                    >
                      <TableCell>{getLevelIcon(log.level)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip label={log.category} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={format(new Date(log.timestamp), 'PPpp')}>
                          <span>{formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: { xs: 200, md: 400 } }} noWrap>
                          {log.message}
                        </Typography>
                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {JSON.stringify(log.metadata).substring(0, 100)}...
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="caption">{log.user_email || '-'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {logs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">No logs found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Feedback Analytics Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            {feedbackAnalytics ? (
              <>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>Total Feedback</Typography>
                        <Typography variant="h4">{feedbackAnalytics.overall?.total_feedback || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          out of {feedbackAnalytics.overall?.total_messages || 0} messages
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ThumbDownIcon color="error" />
                          <Box>
                            <Typography color="text.secondary" gutterBottom>Confirmed Scams</Typography>
                            <Typography variant="h4" color="error.main">
                              {feedbackAnalytics.overall?.confirmed_scams || 0}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ThumbUpIcon color="success" />
                          <Box>
                            <Typography color="text.secondary" gutterBottom>False Positives</Typography>
                            <Typography variant="h4" color="warning.main">
                              {feedbackAnalytics.overall?.false_positives || 0}
                            </Typography>
                          </Box>
                        </Box>
                        {feedbackAnalytics.overall?.accuracy_rate && (
                          <Typography variant="body2" color="success.main">
                            {feedbackAnalytics.overall.accuracy_rate}% accuracy rate
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Feedback by Risk Level */}
                {feedbackAnalytics.byRiskLevel?.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom>Feedback by Risk Level</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Risk Level</TableCell>
                            <TableCell align="right">Confirmed Scams</TableCell>
                            <TableCell align="right">False Positives</TableCell>
                            <TableCell align="right">Total Feedback</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {feedbackAnalytics.byRiskLevel.map((row) => (
                            <TableRow key={row.risk_level}>
                              <TableCell>
                                <Chip 
                                  label={row.risk_level} 
                                  size="small"
                                  color={row.risk_level === 'high' ? 'error' : row.risk_level === 'medium' ? 'warning' : 'success'}
                                />
                              </TableCell>
                              <TableCell align="right">{row.confirmed_scams}</TableCell>
                              <TableCell align="right">{row.false_positives}</TableCell>
                              <TableCell align="right">{row.total}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {/* Recent Feedback */}
                {feedbackAnalytics.recentFeedback?.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom>Recent Feedback</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Channel</TableCell>
                            <TableCell>Risk</TableCell>
                            <TableCell>Feedback</TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>User</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Message Preview</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {feedbackAnalytics.recentFeedback.slice(0, 20).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Typography variant="caption">
                                  {format(new Date(item.feedback_at), 'MM/dd HH:mm')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Chip label={item.channel} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.risk_level || 'unknown'}
                                  size="small"
                                  color={item.risk_level === 'high' ? 'error' : item.risk_level === 'medium' ? 'warning' : 'default'}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={item.user_feedback === 'confirmed_scam' ? <ThumbDownIcon /> : <ThumbUpIcon />}
                                  label={item.user_feedback === 'confirmed_scam' ? 'Scam' : 'Legit'}
                                  size="small"
                                  color={item.user_feedback === 'confirmed_scam' ? 'error' : 'success'}
                                />
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                <Typography variant="caption">{item.user_email}</Typography>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Typography variant="caption" sx={{ maxWidth: 200 }} noWrap>
                                  {item.message_preview}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            ) : (
              <Typography color="text.secondary">No feedback data available yet.</Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SystemHealth;
